import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BASE_URL } from "./config";
import { logError } from "./logError";
import Header from "./components/Header";
import Footer from "./components/Footer";
import AlertBanner from "./components/AlertBanner";
import ReCAPTCHA from "react-google-recaptcha";
import { JS_URL } from "./config";

import amexSymbol from "./assets/images/symbol_american-express.svg";
import dinersSymbol from "./assets/images/symbol_diners-club-international.svg";
import discoverSymbol from "./assets/images/symbol_discover.svg";
import mastercardSymbol from "./assets/images/symbol_ma.svg";
import visaSymbol from "./assets/images/symbol_visa.svg";

function Home() {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const orderId = Array.from(searchParams.entries()).reduce((acc, [key, value]) => {
    return key.toLowerCase() === "orderid" ? value : acc;
  }, null);
  const [payPageId, setPayPageId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [paymentLoader, setPaymentLoader] = useState(false);
  const [data, setData] = useState([]);
  const [dataError, setDataError] = useState({ heading: null, msg: null, type: null });
  const [isData, setIsData] = useState(false);
  const [error, setError] = useState("");
  const didRun = useRef(false);
  const hasSubmitted = useRef(false);

  const [isNameValid, setIsNameValid] = useState(false);
  const [title, setTitle] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [iframeVersion, setIframeVersion] = useState(null);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [captchaError, setCaptchaError] = useState("");

  const openModal = () => setIsModalOpen(true);
  const isCaptchaVerifiedRef = useRef(isCaptchaVerified);
  const baseCSMessage =
    "<p>Please reply to the email you received or contact ASCE Customer Service for assistance </p><ul class='grid mb-4'><li class='g-col-12 g-col-md-6'><dl><dt><strong>by email</strong></dt><dd class='ms-0'><a href='mailto:member@asce.org' title='member@asce.org' class='-arrow-link'>member@asce.org</a> </dd></dl></li><li class='g-col-12 g-col-md-6'><dl><dt><strong>by phone</strong></dt><dd class='ms-0'><ul class='list-inline mb-0'><li class='list-inline-item'><a class='-arrow-link' href='tel:1-800-548-2723' title='800-548-2723'>800-548-2723</a>, or</li><li class='list-inline-item'><a class='-arrow-link' href='tel:+1-703-295-6300' title='+1-703-295-6300'>+1-703-295-6300</a> (outside the U.S.).</li></ul></dd></dl></li></ul><p class='mb-0'><small>Representatives are available to provide assistance between the hours of 9 a.m. &dash; 6 p.m. EST, Monday through Friday. To help us resolve your issue quickly, please include your order number or your full name and address in your message.</small></p>";

  const handleCaptchaChange = (value) => {
    if (value) {
      setIsCaptchaVerified(true);
      setCaptchaError("");
    }
  };

  const validateNameOnCard = () => {
    const name = document.getElementById("nameOnCard")?.value?.trim() || "";

    if (!name) {
      setError("Cardholder name is required.");
      setIsNameValid(false);
      return false;
    } else if (name.length < 2) {
      setError("Enter a valid name.");
      setIsNameValid(false);
      return false;
    } else if (!/^[A-Za-z\s.'-]+$/.test(name)) {
      setError("Name contains invalid characters.");
      setIsNameValid(false);
      return false;
    }

    setError("");
    setIsNameValid(true);
    return true;
  };

  useEffect(() => {
    isCaptchaVerifiedRef.current = isCaptchaVerified;
  }, [isCaptchaVerified]);

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    const fetchData = async () => {
      try {
        setIsPageLoading(true);
        setIsData(true);
        const result = await fetch(`${BASE_URL}/Order/GetOrderDetail?OrderID=${orderId}`);
        const output = await result.json();
        const parsedData = JSON.parse(output.Content);
        if (parsedData.success === false) {
          setDataError({
            heading: "We're sorry - something went wrong!",
            msg: `<strong>${parsedData.errorMessage}</strong>${baseCSMessage}`,
            type: "error",
          });

          setIsData(false);
        } else {
          if (parsedData.ordertotalbalanceamount === "0") {
            const isInvalidOrder = parsedData.message?.toLowerCase().includes("invalid order number");
            setDataError({
              heading: isInvalidOrder ? parsedData.message : "Payment received",
              msg: isInvalidOrder ? baseCSMessage : parsedData.message,
              type: isInvalidOrder ? "error" : "success",
            });
            setIsData(false);
          } else {
            if (parsedData.iseligibleforpayment === "N") {
              setDataError({
                heading: parsedData.message,
                msg: baseCSMessage,
                type: "warning",
              });
              setIsData(false);
            } else {
              setData(parsedData);
              setIsData(true);
            }
          }
        }
      } catch (err) {
        await logError({
          message: err.message,
        });
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchData();
  }, [orderId]);

  useEffect(() => {
    fetchPaymentDetail();
  }, []);

  // <----- Dynamic Page title ----->>
  useEffect(() => {
    if (isData && data?.billCustomerName) {
      document.title = `${data.billCustomerName} | ASCE Pay`;
    } else {
      document.title = "ASCE Pay";
    }
  }, [isData, data]);

  const fetchPaymentDetail = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/merchant/getLowvalueToken`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const output = await response.json();
      const parsedContent = JSON.parse(output.Content);
      setPayPageId(parsedContent.payPageID);
    } catch (err) {
      await logError({
        message: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshPayframe = () => {
    hasSubmitted.current = false;

    const container = document.getElementById("payframe");
    if (container) container.innerHTML = "";

    if (window.grecaptcha) {
      window.grecaptcha.reset();
    }
    setIsCaptchaVerified(false);
    setCaptchaError("");

    setIframeVersion((v) => v + 1);
  };

  // <----- logic to iframe details ----->
  useEffect(() => {
    if (!payPageId || !data) return;
    const payframeDiv = document.getElementById("payframe");

    if (!payframeDiv) {
      return;
    }
    // Load Worldpay eProtect script
    const script = document.createElement("script");
    script.src = JS_URL;
    script.async = true;
    script.onload = () => {
      if (window.EprotectIframeClient) {
        const payframeClientCallback = async (response) => {
          if (hasSubmitted.current) return;
          hasSubmitted.current = true;
          const sumINV = data.details
            ? data.details.filter((item) => item.subsystem === "INV").reduce((acc, item) => acc + (Number(item.actualtotalamount) || 0), 0)
            : 0;
          const sumNonINV = data.details
            ? data.details.filter((item) => item.subsystem !== "INV").reduce((acc, item) => acc + Number(item.actualtotalamount || 0), 0)
            : 0;
          let isINV = true;
          let isNonINV = true;

          if (sumINV === "0") {
            isINV = false;
          }
          if (sumNonINV === "0") {
            isNonINV = false;
          }

          if (!validateNameOnCard()) {
            return false;
          }

          if (!isCaptchaVerifiedRef.current) {
            setCaptchaError("Complete the reCAPTCHA to proceed with payment.");
            return;
          }

          if (response.paypageRegistrationId) {
            const token = response.paypageRegistrationId;
            try {
              setPaymentLoader(true);
              const res = await fetch(`${BASE_URL}/PayOrder/EprotectOrderPayment`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  paypageRegistrationId: token,
                  TotalAmount: data.ordertotalbalanceamount,
                  currency: "USD",
                  nameOnCard: document.getElementById("nameOnCard").value,
                  invAmount: sumINV,
                  nonInvAmount: sumNonINV,
                  isINV: isINV,
                  isNonINV: isNonINV,
                  ExpirationMonth: response.expMonth,
                  ExpirationYear: "20" + response.expYear,
                  CardType: response.type,
                  CardNumber: response.firstSix + "******" + response.lastFour,
                  OrderId: data.orderno,
                  PaymentType: "eProtect",
                  OrderSource: "ecommerce",
                  ReportGroup: "ecommerce",
                  BillMasterCustomerID: data.billMasterCustomerId,
                  CreditCardDetail: {
                    ExpirationMonth: response.expMonth,
                    ExpirationYear: "20" + response.expYear,
                    NameOnCard: document.getElementById("nameOnCard").value,
                    CardNumber: response.firstSix + "******" + response.lastFour,
                  },
                }),
              });
              const result = await res.json();
              const resultContent = JSON.parse(result.Content);
              const thankYouContent = {
                responseValue: resultContent,
                orderDetails: data,
              };
              if (resultContent.success === true) {
                localStorage.setItem("Details", JSON.stringify(thankYouContent));
                navigate(`/thankyou`);
              } else {
                refreshPayframe();
                if (resultContent.errorMessage === "Technical") {
                  setTitle("We're sorry - something went wrong!");
                  setErrorMsg(baseCSMessage);
                  openModal();
                } else {
                  setTitle("We're sorry!");
                  setErrorMsg(resultContent.errorMessage);
                  openModal();
                }
              }

              setPaymentLoader(false);
            } catch (err) {
              await logError({
                message: err.message,
              });
            }
          } else {
            console.error("Error response:", response);
          }
        };

        const configure = {
          paypageId: payPageId,
          style: "worldpayv4", // EnhancedDemoUx.css worldpaymin1
          height: "392",
          reportGroup: "Payment Research",
          div: "payframe",
          callback: payframeClientCallback,
          showCvv: true,
          enhancedUxFeatures: {
            inlineFieldValidations: true,
            enhancedUxVersion: 2,
            expDateValidation: true,
            cVVValidation: true,
            coloredCardNetworkLogos: false,
            numericInputsOnly: false,
          },
          noScrollBar: true,
          label: {
            accountNumber: "Card number",
            expDate: "Card expiration date",
            cvv: "Security code",
          },
          numYears: 8,
          customErrorMessages: {
            "871": "The Card number doesn't seem to be valid. Please double-check and try again.",
            "872": "Card number too short.",
            "873": "Card number too long.",
            "874": "Card number not numeric.",
            "876": "The Card number doesn't seem to be valid. Please double-check and try again.",
            "881": "Security code is not numeric.",
            "882": "Security code too short.",
            "883": "Security code too long.",
            "886": "Card expiration date invalid.",
            "886-month": "Card expiration month invalid.",
            "886-year": "Card expiration year invalid.",
          },
        };
        const payframeClient = new window.EprotectIframeClient(configure);
        payframeClient.autoAdjustHeight();
        document.getElementById("fCheckout").onsubmit = function () {
          if (payframeClient.allInputsEmpty()) return false;
          const orderId = Math.floor(10000 + Math.random() * 90000);
          const message = { id: "5454545454", orderId };
          payframeClient.getPaypageRegistrationId(message);
          return false;
        };
      }
    };
    document.body.appendChild(script);
  }, [payPageId, data, iframeVersion, navigate]);

  return (
    <div className={`app-content page-container${isPageLoading ? " -page-loading" : ""}`}>
      <a href="#maincontent" className="skip-link">
        Skip to main content
      </a>
      <Header isData={isData} isPageLoading={isPageLoading} data={data} />

      <main id="maincontent" aria-label="Page Content">
        <div className="grid-container">
          {isPageLoading ? (
            <div className="g-col-main">
              <h1 className="sr-text">ASCE click to pay</h1>
              <div className="asce-loader -loading">
                <div className="asce-loader-text">Loading content... We appreciate your patience</div>
              </div>
            </div>
          ) : isData ? (
            <section className="payment-grid">
              <div className="payment-grid-inner">
                <h1 className="sr-text">ASCE click to pay</h1>
                <div className="payment-grid-items">
                  <div className="payment-grid-card">
                    <div id="order-info">
                      <div className="payment-grid-card-body">
                        <h2 className="eyebrow">Order details</h2>
                        <ul>
                          <li>
                            <p>
                              <strong>Order number:</strong> {data.orderno}
                            </p>
                          </li>
                          <li>
                            <p>
                              <strong>Customer name:</strong> {data.billCustomerName}
                            </p>
                          </li>
                        </ul>

                        {data.details && data.details.length > 0 ? (
                          <ul className="payment-grid-items-list">
                            {data.details.map((item, index) => (
                              <li key={index} className="payment-grid-items-list-item">
                                <div className="payment-grid-items-list-item-name">
                                  <strong>
                                    <span>{item.description}</span>
                                  </strong>
                                </div>
                                <div className="payment-grid-items-list-item-amount">
                                  <span>${Number(item.actualtotalamount).toFixed(2)}</span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  {isModalOpen && <AlertBanner type="error" title={title} message={errorMsg} statusMessage={false} onClose={() => setIsModalOpen(false)} />}
                </div>
                <div className="payment-grid-details">
                  <div className="payment-grid-card">
                    <div className="payment-grid-card-body">
                      <h2 className="eyebrow">Payment details</h2>
                      <div>
                        <form id="fCheckout" name="fCheckout">
                          {loading ? (
                            <div className="payment-grid-card-body asce-loader -contain -loading">
                              <div className="asce-loader-text">Loading content... We appreciate your patience</div>
                            </div>
                          ) : (
                            <>
                              <div className="payment-grid-instructions">
                                <p>
                                  <em>All fields are required.</em>
                                </p>
                                <p className="mb-0">We honor:</p>
                                <ul className="payment-grid-iconset mb-2">
                                  <li className="payment-grid-iconset-item">
                                    <img src={amexSymbol} className="payment-grid-iconset-item-icon" alt="American Express logo" /> American Express
                                  </li>
                                  <li className="payment-grid-iconset-item">
                                    <img src={dinersSymbol} className="payment-grid-iconset-item-icon" alt="Diners Club International logo" /> Diners Club
                                  </li>
                                  <li className="payment-grid-iconset-item">
                                    <img src={discoverSymbol} className="payment-grid-iconset-item-icon" alt="Discover Card logo" />
                                    Discover
                                  </li>
                                  <li className="payment-grid-iconset-item">
                                    <img src={mastercardSymbol} className="payment-grid-iconset-item-icon" alt="Mastercard logo" /> Mastercard
                                  </li>
                                  <li className="payment-grid-iconset-item">
                                    <img src={visaSymbol} className="payment-grid-iconset-item-icon" alt="Visa logo" /> Visa
                                  </li>
                                </ul>
                                <p>
                                  <small>ASCE only accepts cards issued by U.S. banks</small>
                                </p>
                              </div>
                              <div className="form-field">
                                <label htmlFor="nameOnCard">Cardholder name</label>
                                <input
                                  id="nameOnCard"
                                  type="text"
                                  className={`form-control ${error ? "-invalid" : ""}`}
                                  tabIndex="0"
                                  name="Cardholder Name"
                                  aria-describedby="nameOnCard-status"
                                  aria-invalid={error ? "true" : "false"}
                                  onBlur={validateNameOnCard}
                                  onChange={(e) => {
                                    const value = e.target.value.trim();

                                    if (!value) {
                                      setError("Cardholder name is required.");
                                      setIsNameValid(false);
                                      return;
                                    }
                                    validateNameOnCard();
                                  }}
                                />
                                {error && (
                                  <div id="nameOnCard-status" className="error-message" role="status" aria-live="polite">
                                    {error}
                                  </div>
                                )}
                              </div>
                              <div id="payframe"></div>
                              <fieldset className="form-field" aria-describedby="captcha-help">
                                <legend className="sr-text">Human verification</legend>
                                <ReCAPTCHA sitekey="6LcQ1FAqAAAAAIyBJg7FA17kQ00BiSWA61AW5nPi" onChange={handleCaptchaChange} />
                                <small id="captcha-help" className="sr-text">
                                  Complete the reCAPTCHA to proceed with payment.
                                </small>
                              </fieldset>
                              {captchaError && <div className="d-block invalid-feedback error-message">{captchaError}</div>}
                            </>
                          )}
                          <div id="liveRegion" role="status" aria-live="polite"></div>
                          <button
                            type="submit"
                            id="submitId"
                            className={`btn ${paymentLoader ? "loading" : ""}`}
                            aria-live={paymentLoader ? "polite" : undefined}
                            aria-busy={paymentLoader ? "true" : undefined}
                            aria-label={paymentLoader ? "Submitting, please wait" : undefined}
                            disabled={loading || isPageLoading || !isCaptchaVerified || paymentLoader || !isNameValid}
                          >
                            Pay ${loading || isPageLoading ? "" : Number(data.ordertotalbalanceamount).toFixed(2)}
                            {paymentLoader && <span className="sr-text"> Submitting…</span>}
                          </button>
                        </form>
                        <div id="eprotect-error" className="d-none" role="status" aria-live="polite">
                          <p id="eprotect-error-message" className="error-message"></p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          ) : (
            <section className="messages-component" role="alert" aria-live="assertive">
              <div className="messages-component-inner">
                <AlertBanner
                  type={dataError.type}
                  title={dataError.heading}
                  message={dataError.msg}
                  dismissible={false}
                  onClose={() => setIsModalOpen(false)}
                />
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default Home;
